/**
 * Who is buying, read off a form — one way or the other, and never both.
 *
 * Lifted from `gallery/sellWizard.model.ts` when the collector's own sale
 * became its second caller (`sell/ownerSale.model.ts`, OND-173), which is the
 * register `parseMoneyMinor` moved in and for the same reason: the rule it
 * holds is not a formatting convenience but the client half of a refusal the
 * database makes, and two copies of it would drift. `sellWizard.model.ts`
 * re-exports both names so its own callers and tests are untouched.
 *
 * THE RULE: `buyerMode` decides which field is READ, not merely which is
 * shown. So a name left behind by switching the toggle cannot be submitted
 * beside an id, and the XOR the server refuses — `effectSaleSchema` and
 * `effectOwnerSaleSchema` both carry the same `.refine`, and
 * `ondo.effect_sale_authorised()` raises it as a 22023 — is unreachable from
 * either form rather than merely refused by it. That is the house rule *an
 * ONDO id is attached, never discovered* met from the form's side.
 *
 * NEITHER SIDE LOOKS ANYBODY UP. The id arm submits an id the seller WAS
 * GIVEN; the name arm submits their own typing against no identity at all.
 */

/** Which field names the buyer. The designs call these "Match on network" and "Create new owner". */
export type BuyerMode = "ondo" | "name";

/**
 * The buyer, as a discriminated value rather than a boolean.
 *
 * Both forms build their submit from the SAME answer their Continue gate used,
 * so the gate and the request cannot drift — a submit that silently dropped a
 * buyer is the one bug in this area worth fearing.
 */
export type NamedBuyer =
  | { kind: "ondo"; ondoId: string }
  | { kind: "name"; name: string };

/** The three fields of any draft this reads. Both sale drafts satisfy it structurally. */
export interface BuyerFields {
  buyerMode: BuyerMode;
  buyerName: string;
  buyerOndoId: string;
}

/**
 * The buyer, or null when the field the mode names is empty or malformed.
 *
 * The id is upper-cased and shape-checked here rather than at the server,
 * which saves a request on a typo — and, on the consigned form, saves one of
 * ten probes an hour. The shape is the one `ondoIdField()` validates.
 */
export function namedBuyer(fields: BuyerFields): NamedBuyer | null {
  if (fields.buyerMode === "ondo") {
    const ondoId = fields.buyerOndoId.trim().toUpperCase();
    return /^ONDO-\d{4,}$/.test(ondoId) ? { kind: "ondo", ondoId } : null;
  }

  const name = fields.buyerName.trim();
  return name === "" ? null : { kind: "name", name };
}
